import * as React from "react";
import { BaseRadioGroup, RadioGroupItem } from "../base/base-radio-group";
import { cn } from "../../utils/cn";
import { FontStyles } from "../../utils/font-styles";

export interface RadioOption {
  value: string;
  label: string;
  id: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  // Required props
  value: string | undefined;
  onValueChange: (value: string) => void;
  options: RadioOption[];

  // Optional layout
  layout?: "horizontal" | "vertical";

  // Optional label & validation
  label?: string;
  showRequiredAsterisk?: boolean;
  state?: "default" | "error";
  message?: string;

  // Optional styling
  labelSize?: "default" | "large";
  className?: string;

  // Pass-through to BaseRadioGroup
  disabled?: boolean;
  name?: string;
}

export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      value,
      onValueChange,
      options,
      layout = "horizontal",
      label,
      showRequiredAsterisk = false,
      state = "default",
      message,
      labelSize = "default",
      className,
      disabled,
      name,
    },
    ref,
  ) => {
    const containerClass = layout === "horizontal" ? "flex flex-row gap-5 md:gap-6.5" : "flex flex-col gap-3";

    return (
      <BaseRadioGroup
        ref={ref}
        value={value}
        onValueChange={onValueChange}
        label={label}
        showRequiredAsterisk={showRequiredAsterisk}
        state={state}
        message={message}
        labelSize={labelSize}
        className={className}
        disabled={disabled}
        name={name}
      >
        <div className={containerClass}>
          {options.map((option) => (
            <div key={option.id} className="flex items-center gap-2.5">
              <RadioGroupItem value={option.value} id={option.id} disabled={option.disabled} />
              <label
                className={cn("cursor-pointer", FontStyles["body-s"], option.disabled && "text-light-slate")}
                htmlFor={option.id}
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>
      </BaseRadioGroup>
    );
  },
);

RadioGroup.displayName = "RadioGroup";
